import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';
import { Sort } from '@angular/material/sort';
import { FeatureFlag } from '@core/enums';
import { Store } from '@ngxs/store';
import { RelatedToType } from '@shared/enums';
import { getHeaderLabel, getLinkedFieldValue as _getLinkedFieldValue, isLinkedFieldColumn } from '@shared/functions';
import { AccountState } from '@state/account/state';
import { FieldDef } from 'app/core/models';
import { LayoutService } from 'app/layout/layout.service';
import { DataTableModel, SummarizeMethod, TimeSeriesCadenceOptions } from 'app/modules/reporting/reporting.types';
import { SelectableFieldDef } from 'app/shared/components/column-selector/column-selector.types';
import { GraphqlFilterSort } from 'app/shared/models/graphql';
import get from 'lodash-es/get';

@Component({
  selector: 'app-reporting-builder-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTableComponent implements OnChanges {
  @Input() data: DataTableModel;
  @Input() options: any;
  @Input() fields: FieldDef[];
  @Input() rootContainerCss: any;
  @Input() set columns(value: string[]) {
    let hasTotal = false;
    if (this.data) {
      if (this.data.timeSeries?.columns) {
        hasTotal = this.data.timeSeries.columns.some(col => 
          typeof col === 'string' ? col === 'Total' : (col as any).name === 'Total'
        );
      }
      if (!hasTotal && this.data.rows && this.data.rows.length > 0) {
        hasTotal = this.data.rows.some(row => row.hasOwnProperty('Total'));
      }
    }
    this.hasTotalColumn = hasTotal;
    
    if (this.hasTotalColumn && value[0] !== 'Total') {
      this._columns = ['Total', ...value.filter(col => col !== 'Total')];
    } 
    else if (!this.hasTotalColumn && value.includes('Total')) {
      this._columns = value.filter(col => col !== 'Total');
    }
    else {
      this._columns = value;
    }
    
    this.selectedFields = this.fields.filter(({ name }) => this._columns.includes(name));
    this.columnDefinitions = this._columns;
  }
  @Input() isTimeSeries = false;
  @Input() canShowGroupOptions = false;
  @Input() timeSeriesFilterIsValid = false;
  @Input() cadence: TimeSeriesCadenceOptions;
  @Input() summarizeMethod: SummarizeMethod;
  @Input() relatedToType: RelatedToType;

  @Input('groupByFieldsOptions') set _groupByFieldsOptions(value: FieldDef[]) {
    this.groupByFieldsOptions = value.map((field) => ({
      ...field,
      selected: this.groupByFields?.map(({ field }) => field).includes(field.name),
    }));
  }
  @Input() groupByFields: GraphqlFilterSort[];
  @Input() relatedToLabel: string;

  @Output() pageChanged = new EventEmitter<PageEvent>();
  @Output() sortChanged = new EventEmitter<Sort>();
  @Output() columnsChanged = new EventEmitter<CdkDragDrop<string>>();
  @Output() cadenceChanged = new EventEmitter<TimeSeriesCadenceOptions>();
  @Output() summarizeMethodChanged = new EventEmitter<SummarizeMethod>();
  @Output() groupByFieldsChanged = new EventEmitter<string[]>();

  @ViewChild('selectField') groupByselectField: MatSelect;

  selectedFields: FieldDef[] = [];
  position: ConnectedPosition = {
    originX: 'start',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'bottom',
  };
  groupByFieldsOptions: SelectableFieldDef[] = [];
  hasNestedFiltersOn = false;
  hasTotalColumn = false;
  columnDefinitions: string[] = [];
  getHeaderLabel = getHeaderLabel;
  isLinkedFieldColumn = isLinkedFieldColumn;

  private _columns: string[];

  get columns(): string[] {
    return this._columns;
  }

  constructor(
    private readonly layoutService: LayoutService,
    private readonly store: Store,
  ) {
    this.hasNestedFiltersOn = this.store
      .selectSnapshot(AccountState.getAccount)
      ?.features.includes(FeatureFlag.NESTED_FILTERS);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data && this.data) {  
      let hasTotal = false;
      
      if (this.data.timeSeries?.columns) {
        hasTotal = this.data.timeSeries.columns.some(col => 
          typeof col === 'string' ? col === 'Total' : (col as any).name === 'Total'
        );
      }
      if (!hasTotal && this.data.rows && this.data.rows.length > 0) {
        hasTotal = this.data.rows.some(row => row.hasOwnProperty('Total'));
      }
      
      this.hasTotalColumn = hasTotal;
      
      if (this.hasTotalColumn && this._columns && this._columns[0] !== 'Total') {
        this._columns = ['Total', ...this._columns.filter(col => col !== 'Total')];
        this.selectedFields = this.fields.filter(({ name }) => this._columns.includes(name));
        this.columnDefinitions = this._columns;
      }
      else if (!this.hasTotalColumn && this._columns && this._columns.includes('Total')) {
        this._columns = this._columns.filter(col => col !== 'Total');
        this.selectedFields = this.fields.filter(({ name }) => this._columns.includes(name));
        this.columnDefinitions = this._columns;
      }
    }
  }

  isTotalRow(row: any): boolean {
    const isNonGroupedTotal = row.Total === 'Total';
    const isGroupedTotal = this.groupByFields?.length > 0 && Object.keys(row).some((x) =>
      this.groupByFields.map((y) => y.field).includes(x) ? row?.[x] === 'Total' : false,
    );
    
    return isNonGroupedTotal || isGroupedTotal;
  }

  openSelectField(): void {
    this.layoutService.emitCommon('openSelectField', true, 'subject');
  }

  openFiltersTab(): void {
    this.layoutService.emitCommon('openFiltersTab', true, 'subject');
  }

  onSaveColumns(fields: SelectableFieldDef[]): void {
    this.groupByFieldsChanged.emit(fields.map(({ name }) => name));
  }

  mutateToFieldArray(value: GraphqlFilterSort[]): string[] {
    return value?.map(({ field }) => field) || [];
  }

  dropColumns(event: CdkDragDrop<string>): void {
    this.columnsChanged.emit(event);
  }

  handleShapeShifter(fieldDef: FieldDef, value: any): any {
    if (fieldDef.meta.shapeShifter.type === 'select') {
      let label =
        fieldDef.meta.shapeShifter.meta.options.find((x) => x.id == value[fieldDef.name])?.name ||
        this.options[`${fieldDef.meta.shapeShifter.meta.option_name}_deleted`]?.find(
          (x) => x.id == value[fieldDef.name],
        )?.name;

      if (label) {
        return label;
      }
    }

    return value[fieldDef.name];
  }

  getLinkedFieldValue(object: any, fieldName: string): { label: string; [key: string]: any }[] {
    return _getLinkedFieldValue(object, fieldName, {
      fieldDefs: this.fields,
      options: this.options,
    });
  }

  getColumnValue(row: any, columnName: string): string {
    const value = row[columnName];
    if (value && typeof value === 'object' && 'label' in value) {
      return value.label;
    }
    return value;
  }
}
